import { Truck, Users, Clock, TrendingUp, MapPin, Star, ArrowRight, Briefcase, Shield, CheckCircle, Award } from 'lucide-react'
import SEOHead from '../components/SEOHead'
import { Link } from 'react-router-dom'
import siteHeaderBg from '../assets/images/siteheaderbg.jpg'

export default function Home() {
  return (
    <div>
      <SEOHead 
        title="C9 Recruitment - UK Transport & Logistics Recruitment Specialists"
        description="Leading UK recruitment agency specializing in drivers, engineers, and logistics professionals. Find your next opportunity or hire top talent today."
        keywords="recruitment, transport, logistics, drivers, engineers, HGV, UK jobs, staffing, employment"
        canonical="/"
      />
      {/* Hero Section */}
      <section className="relative text-white section-padding overflow-hidden">
        {/* Background with Transport Theme */}
        <div className="absolute inset-0">
          {/* Transport Background Image */}
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat" 
            style={{ 
              backgroundImage: `url(${siteHeaderBg})`,
              backgroundAttachment: 'fixed'
            }}
          ></div>
          {/* Dark overlay for text readability */}
          <div className="absolute inset-0 bg-black/50"></div>
        </div>
        
        {/* Content */}
        <div className="container-full relative z-10">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl xl:text-7xl font-black mb-6 animate-fade-in max-w-5xl mx-auto">
              Efficient Recruitment Solutions 
              <span className="text-c9-orange"> for Your Business</span>
            </h1>
            <p className="text-xl md:text-2xl xl:text-3xl mb-8 text-gray-200 animate-slide-up max-w-4xl mx-auto">
              At C9 Recruitment, we understand that finding reliable staff can be a major challenge. 
              That's why we've made it our mission to provide an efficient, dependable service that 
              delivers results when you need them most.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-up">
              <Link to="/clients" className="btn-primary text-lg px-8 py-4 flex items-center justify-center whitespace-nowrap">
                Hire with Us
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <Link to="/candidates" className="btn-outline bg-white text-c9-blue border-white hover:bg-gray-100 text-lg px-8 py-4 flex items-center justify-center whitespace-nowrap">
                View Opportunities
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-gray-50 section-padding">
        <div className="container-full">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Drivers Available */}
            <div className="text-center group">
              <div className="flex justify-center mb-4">
                <div className="bg-c9-orange/5 p-3 rounded-full">
                  <Users className="h-6 w-6 text-c9-orange" />
                </div>
              </div>
              <div className="text-4xl lg:text-5xl font-bold text-c9-orange mb-2">30,000+</div>
              <div className="text-lg font-semibold text-c9-blue mb-2">Drivers Available</div>
              <div className="text-gray-600">In our comprehensive database</div>
            </div>
            
            {/* Award Winning */}
            <div className="text-center group">
              <div className="flex justify-center mb-4">
                <div className="bg-c9-orange/5 p-3 rounded-full">
                  <Award className="h-6 w-6 text-c9-orange" />
                </div>
              </div>
              <div className="text-4xl lg:text-5xl font-bold text-c9-orange mb-2">Award</div>
              <div className="text-lg font-semibold text-c9-blue mb-2">Winning Service</div>
              <div className="text-gray-600">Transport & Logistics Agency of the Year 2023</div>
            </div>
            
            {/* 24/7 Support */}
            <div className="text-center group">
              <div className="flex justify-center mb-4">
                <div className="bg-c9-orange/5 p-3 rounded-full">
                  <Clock className="h-6 w-6 text-c9-orange" />
                </div>
              </div>
              <div className="text-4xl lg:text-5xl font-bold text-c9-orange mb-2">24/7</div>
              <div className="text-lg font-semibold text-c9-blue mb-2">Support</div>
              <div className="text-gray-600">Always here when you need us most</div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="section-padding">
        <div className="container-full">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-c9-blue mb-4">
              Our Solutions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Proven capabilities and expertise in providing fully tailored solutions 
              that deliver cost-effective and beneficial long-term results.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "Contingency Recruitment",
                description: "No-fee upfront solution. You only pay when we successfully place a candidate.",
                icon: <Users className="h-8 w-8 text-c9-orange" />
              },
              {
                title: "Executive Search",
                description: "Specialized recruitment for hard-to-find executive-level candidates.",
                icon: <Award className="h-8 w-8 text-c9-orange" />
              },
              {
                title: "Temporary Staffing",
                description: "Experienced and skilled temporary staff screened to meet your specific needs.",
                icon: <Clock className="h-8 w-8 text-c9-orange" />
              }
            ].map((service, index) => (
              <div key={index} className="card p-8 text-center hover:scale-105 transition-transform">
                <div className="flex justify-center mb-4">{service.icon}</div>
                <h3 className="text-xl font-semibold text-c9-blue mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-6">{service.description}</p>
                <Link to="/clients" className="text-c9-orange font-semibold hover:underline">
                  Learn More →
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-c9-orange text-white section-padding">
        <div className="container-full text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Transform Your Recruitment?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Join hundreds of satisfied clients who trust C9 Recruitment 
            for their staffing needs.
          </p>
          <Link to="/contact" className="btn-secondary text-lg px-8 py-4 flex items-center justify-center whitespace-nowrap">
            Get Started Today
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </section>
    </div>
  )
}
